/**
 * Cabinet Comptable Hooks
 * ISO 27001 A.9.2.3 - Management of privileged access rights
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cabinetApi, ClientsQueryParams } from '../api/cabinet.api';
import type {
  InviteClientDTO,
  AddClientDTO,
  UpdateClientDTO,
  FirmClientStatus,
} from '../types';

// Query keys
const CABINET_KEYS = {
  all: ['cabinet'] as const,
  clients: (params?: ClientsQueryParams) => [...CABINET_KEYS.all, 'clients', params] as const,
  client: (id: string) => [...CABINET_KEYS.all, 'client', id] as const,
  stats: () => [...CABINET_KEYS.all, 'stats'] as const,
  myFirms: () => [...CABINET_KEYS.all, 'my-firms'] as const,
};

/**
 * Hook to fetch cabinet clients
 */
export function useCabinetClients(params: ClientsQueryParams = {}) {
  return useQuery({
    queryKey: CABINET_KEYS.clients(params),
    queryFn: () => cabinetApi.getClients(params),
    staleTime: 30000,
  });
}

/**
 * Hook to fetch a single client
 */
export function useCabinetClient(id: string) {
  return useQuery({
    queryKey: CABINET_KEYS.client(id),
    queryFn: () => cabinetApi.getClient(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch cabinet statistics
 */
export function useCabinetStats() {
  return useQuery({
    queryKey: CABINET_KEYS.stats(),
    queryFn: () => cabinetApi.getStats(),
    staleTime: 60000,
  });
}

/**
 * Hook to fetch firms managing the current client
 */
export function useMyFirms() {
  return useQuery({
    queryKey: CABINET_KEYS.myFirms(),
    queryFn: () => cabinetApi.getMyFirms(),
    staleTime: 60000,
  });
}

/**
 * Hook to invite a new client
 */
export function useInviteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: InviteClientDTO) => cabinetApi.inviteClient(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.clients() });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.stats() });
    },
  });
}

/**
 * Hook to add an existing client
 */
export function useAddExistingClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AddClientDTO) => cabinetApi.addExistingClient(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.clients() });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.stats() });
    },
  });
}

/**
 * Hook to update a client
 */
export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateClientDTO }) =>
      cabinetApi.updateClient(id, dto),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.clients() });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.client(id) });
    },
  });
}

/**
 * Hook to suspend a client
 */
export function useSuspendClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cabinetApi.suspendClient(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.clients() });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.client(id) });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.stats() });
    },
  });
}

/**
 * Hook to reactivate a client
 */
export function useReactivateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cabinetApi.reactivateClient(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.clients() });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.client(id) });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.stats() });
    },
  });
}

/**
 * Hook to archive a client
 */
export function useArchiveClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cabinetApi.archiveClient(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.clients() });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.client(id) });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.stats() });
    },
  });
}

/**
 * Hook to cancel an invitation
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cabinetApi.cancelInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.clients() });
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.stats() });
    },
  });
}

/**
 * Hook to accept an invitation
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => cabinetApi.acceptInvitation(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CABINET_KEYS.myFirms() });
    },
  });
}

/**
 * Hook to convert to accounting firm
 */
export function useConvertToFirm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => cabinetApi.convertToFirm(),
    onSuccess: () => {
      // Invalidate user/tenant queries to reflect the new type
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
    },
  });
}

/**
 * Hook to check access permissions
 */
export function useCheckAccess(clientTenantId: string) {
  return useQuery({
    queryKey: [...CABINET_KEYS.all, 'access', clientTenantId],
    queryFn: () => cabinetApi.checkAccess(clientTenantId),
    enabled: !!clientTenantId,
  });
}

// ===== DOSSIER CLIENT HOOKS =====

/**
 * Hook to fetch client dossier summary
 */
export function useClientDossier(clientRelationId: string) {
  return useQuery({
    queryKey: [...CABINET_KEYS.all, 'dossier', clientRelationId],
    queryFn: () => cabinetApi.getClientDossier(clientRelationId),
    enabled: !!clientRelationId,
  });
}

/**
 * Hook to fetch client invoices
 */
export function useClientInvoices(clientRelationId: string) {
  return useQuery({
    queryKey: [...CABINET_KEYS.all, 'dossier', clientRelationId, 'invoices'],
    queryFn: () => cabinetApi.getClientInvoices(clientRelationId),
    enabled: !!clientRelationId,
  });
}

/**
 * Hook to fetch client expenses
 */
export function useClientExpenses(clientRelationId: string) {
  return useQuery({
    queryKey: [...CABINET_KEYS.all, 'dossier', clientRelationId, 'expenses'],
    queryFn: () => cabinetApi.getClientExpenses(clientRelationId),
    enabled: !!clientRelationId,
  });
}

/**
 * Hook to fetch client credit notes
 */
export function useClientCreditNotes(clientRelationId: string) {
  return useQuery({
    queryKey: [...CABINET_KEYS.all, 'dossier', clientRelationId, 'credit-notes'],
    queryFn: () => cabinetApi.getClientCreditNotes(clientRelationId),
    enabled: !!clientRelationId,
  });
}

/**
 * Hook to export client accounting data
 */
export function useExportClientAccounting() {
  return useMutation({
    mutationFn: ({
      clientRelationId,
      options,
    }: {
      clientRelationId: string;
      options: { format: 'FEC' | 'CSV'; startDate?: string; endDate?: string };
    }) => cabinetApi.exportClientAccounting(clientRelationId, options),
  });
}
